import React, { useState, useEffect, useRef, useMemo } from "react";
import PostTestControlMarketSelection from "./PostTestControlMarketSection";
import ViewTestAndControlMarket from "./ViewTestAndControlMarket";
import { Steps, Button, Spin, Typography } from "antd";
import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import Papa from "papaparse";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import PreCampaign from "./PreCampaign";
import VerifyTestInput from "./VerifyTestInput";
import ViewResults from "./ViewResults";
import axiosInstance from "../../../../utils/axiosInstance";
import { GeoLiftJobConfigService } from "../../../../services/jobConfigServiceFactory/GeoLiftJobConfigService";
import { getTenantId } from "../../../../utils/auth";
import { v4 as uuidv4 } from "uuid";
import { JobType } from "../../../../constants/JobTypes";
import { uploadFileToS3 } from "../../../../utils/s3Upload";
import { useSelector, useDispatch } from "react-redux";
import type { RootState } from "../../../../redux/store";
import type { AppDispatch } from "../../../../redux/store";
import AIAgentInsights from "../../../../components/agents/QcAgent/AIAgentInsights";
import { DEFAULT_MEASUREMENT_STATISTICAL_SIGNIFICANCE_THRESHOLD } from "../../../../constants/common";
import TestDesignChat from "./TestDesignChat";
import {
  selectHasMatched,
  selectSessionFetched,
  selectSessionLoading,
  selectSessionExists,
  setHasMatched,
  setSessionLoading,
  setSessionFetched,
  setSessionExists,
  setMessages,
  clearSession,
  resetForStudySwitch,
} from "../../../../redux/testDesignSlice";
import { CheckCircleFilled } from "@ant-design/icons";

dayjs.extend(customParseFormat);

interface MarketData {
  Holdout: number;
  ID: number;
  location: string;
  duration: number;
  EffectSize: number;
  Power: number;
  AvgScaledL2Imbalance: number;
  Investment: number;
  AvgATT: number;
  Average_MDE: number;
  ProportionTotal_Y: number;
  rank: number;
  abs_lift_in_perc: number;
  correlation: number;
}
const GeoLiftStudy: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const geoLiftJobConfigService = new GeoLiftJobConfigService();
  const urlId = useParams<{ id: string }>();
  const id = location?.state?.jobConfigId ?? urlId.id;
  const dispatch = useDispatch<AppDispatch>();
  const JobName = location?.state?.jobName;
  const jobModelParameterConfig = location?.state?.modelParameterConfig;
  const [current, setCurrent] = useState(0);
  const verifyTestInputRef = useRef<{ submitForm: () => void } | null>(null);
  const contentContainerRef = useRef<HTMLDivElement>(null);
  const sessionFetchedForJobRef = useRef<string | null>(null);
  const currentRef = useRef(0);
  const showTestDesignStepRef = useRef(false);

  const scrollContentToTop = () => {
    if (contentContainerRef.current) {
      contentContainerRef.current.scrollTop = 0;
    }
  };
  const [testMarkets, setTestMarkets] = useState<string[]>([]);
  const [jobId, setJobId] = useState<string>(id || "");
  const [selectedMarket, setSelectedMarket] = useState<string>("");
  const [selectedMarketsIncludes, setSelectedMarketsIncludes] = useState<
    string[]
  >([]);
  const [marketData, setMarketData] = useState<MarketData[]>([]);
  const [postTestData, setPostTestData] = useState<any>(null);
  const [allMarketData, setAllMarketData] = useState<
    Record<string, MarketData[]>
  >({});
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode");
  const edit = searchParams.get("edit");
  const isEditMode = edit === "true";
  const isViewMode = !mode && !isEditMode && !!id;
  const isCreateMode = !!mode && !isEditMode;
  const [scmType, setScmType] = useState<string>("single");
  const [regressionType,setRegressionType] = useState<string>("SCM");
  const [fileUploaded, setFileUploaded] = useState(false);
  const preCampaignRef = useRef<any>(null);
  const [fileData, setFileData] = useState<string | undefined>(undefined);
  const [campaignDate, setCampaignDate] = useState<Dayjs | null>(null);
  const [hasDataChanged, setHasDataChanged] = useState(false);
  const [initialPreCampaignData, setInitialPreCampaignData] = useState<any>(null);
  const hasInitialDataBeenCaptured = useRef(false);
  const [isSales, setIsSales] = useState<boolean>(jobModelParameterConfig?.parameters?.isSales ?? true);



  const getNextButtonLabel = () => {
    const isEvaluateMode = mode === "evaluate";
    const currentStepId = steps[current]?.id;

    if (currentStepId === "test-design") return isViewMode ? "Next" : "Execute Test";
    if (currentStepId === "results") return "View AI Insights";
    if (edit === "true" && currentStepId === "upload") return "Update Job";
    if (!isEvaluateMode && currentStepId === "verify") return "Create Control & Test Market";
    return "Next";
  };
  const [csvData, setCsvData] = useState<any[]>([]);
  const [refreshMarketData, setRefreshMarketData] = useState(0);

  const recommendationStatus = searchParams.get("recStatus");
  const buyType = location.state?.buyType;
  const [resolvedBuyType, setResolvedBuyType] = useState<string | undefined>(location.state?.buyType);
  const [columnMappings, setColumnMappings] = useState<{
    dateColumn: string;
    locationColumn: string;
    kpiColumn: string;
  }>({
    dateColumn: "date",
    locationColumn: "location",
    kpiColumn: "Y",
  });
  const [columnMappingsLoaded, setColumnMappingsLoaded] = useState(false);

  const isNextDisabled = () => {
    const stepId = steps[current]?.id;

    switch (stepId) {
      case "test-design":
        return false;

      case "upload": {
        const baseValidation =
          !fileUploaded ||
          !preCampaignData.geoLiftStudyName ||
          preCampaignData.geoLiftStudyName.trim() === "" ||
          // "Select Markets" is shown as a required field (red asterisk) for
          // scmType "single" (see PreCampaign.tsx), but was never actually
          // enforced here - Next was enabled even with zero markets picked.
          (scmType === "single" && selectedMarketsIncludes.length === 0);
        if (preCampaignData.buyType === "national") {
          return (
            baseValidation ||
            !preCampaignData.campaignStartDate ||
            !preCampaignData.bauSpent ||
            // Campaign Start Date is outside the uploaded data's date range -
            // there's no valid pre/post split possible, so block study
            // creation rather than just warning (unlike a merely-unmatched
            // date, where the nearest available date is used instead).
            !!preCampaignData.campaignDateOutOfRange
          );
        }
        return baseValidation;
      }

      case "verify":
        return (
          !csvData.length ||
          !columnMappings.dateColumn ||
          !columnMappings.locationColumn ||
          !columnMappings.kpiColumn
        );

      case "power":
        return testMarkets.length === 0 || !selectedMarket;

      default:
        return false;
    }
  };
  const [preCampaignData, setPreCampaignData] = useState<{
    geoLiftStudyName: string;
    buyType: "national" | "non-national";
    fileList: File[];
    campaignStartDate?: string; // or Dayjs if not formatted
    totalSpent?: string;
    is_ai_qc_enabled: boolean;
    bauSpent: number;
    statSigThreshold: number;
    isSales: boolean | undefined;
    campaignDateOutOfRange?: boolean;
  }>({
    geoLiftStudyName: JobName || "",
    buyType: buyType || (mode === "evaluate" ? "national" : "non-national"),
    fileList: [],
    is_ai_qc_enabled: false,
    bauSpent: 0,
    statSigThreshold: DEFAULT_MEASUREMENT_STATISTICAL_SIGNIFICANCE_THRESHOLD,
    isSales: jobModelParameterConfig?.parameters?.isSales,
  });
  useEffect(() => {
    const checkPostTestData = async () => {
      if (!jobId) return;

      // Skip post-test data check if this is a new job
      // New jobs have mode=evaluate or mode=plan in URL
      const modeParam = searchParams.get("mode");
      const isNewJob = !!modeParam;
      console.log("checkPostTestData - mode:", modeParam, "isNewJob:", isNewJob, "jobId:", jobId);
      if (isNewJob) {
        console.log("Skipping checkPostTestData for new job");
        return;
      }

      // Skip if in edit mode - user is actively editing, not viewing results
      const isEditMode = searchParams.get("edit") === "true";
      if (isEditMode) {
        console.log("Skipping checkPostTestData for edit mode");
        return;
      }

      try {
        // For recommendation view, skip post-test file check and directly fetch output
        const isViewingRecommendation =
          recommendationStatus === "recommendation_success";
        const postFileResponse = !isViewingRecommendation
          ? await geoLiftJobConfigService.getFile(
              `${jobId}/input/geo_lift_post_data.csv`
            )
          : true; // Bypass file check for recommendations

        if (postFileResponse || isViewingRecommendation) {
          const response = await axiosInstance.get(
            `/api/geo-lift/output/${jobId}`
          );
          if (response.data && response.data.result) {
            // Find the index of the results step in the CURRENTly visible steps
            const resultsIndex = steps.findIndex(s => s.id === 'results');

            // Do not auto-advance while user is on the test design tab.
            // In view mode the user navigates step-by-step via the Next button —
            // do not auto-advance. Auto-advance only applies during a new/edit run
            // where skipping to results is the natural UX after the job finishes.
            // Also guard against the stale-closure issue: the steps array captured
            // here may not include test-design (before sessionFetched resolved),
            // so resultsIndex would resolve to the wrong slot (power analysis).
            if (!isViewMode) {
              const isOnTestDesign = showTestDesignStepRef.current && currentRef.current === 0;
              if (!isOnTestDesign && resultsIndex !== -1 && recommendationStatus !== "recommendation_success") {
                setCurrent(resultsIndex);
              }
            }


            const resultData = response.data.result;
            const liftPlot = resultData.lift_plot || {};
            const attPlot = resultData.att_plot || {};
            const tObsValues = Object.values(liftPlot.t_obs || {});
            const cObsValues = Object.values(liftPlot.c_obs || {});
            const salesWithCampaignData = tObsValues.map((val: any) => {
              const num = Number(val);
              return isNaN(num) ? 0 : num;
            });

            const salesWithoutCampaignData = cObsValues.map((val: any) => {
              const num = Number(val);
              return isNaN(num) ? 0 : num;
            });

            // Calculate total sales
            const totalSalesWithCampaign = salesWithCampaignData.reduce(
              (sum: number, val: number) => sum + val,
              0
            );
            const totalSalesWithoutCampaign = salesWithoutCampaignData.reduce(
              (sum: number, val: number) => sum + val,
              0
            );

            // Calculate lift percentage
            const liftPercentage =
              totalSalesWithoutCampaign > 0
                ? ((totalSalesWithCampaign - totalSalesWithoutCampaign) /
                    totalSalesWithoutCampaign) *
                  100
                : 0;

            // Calculate ROAS and incremental sales
            const incrementalSales =
              totalSalesWithCampaign - totalSalesWithoutCampaign;
            const adSpend = resultData.adSpend || 0;
            const roas = adSpend > 0 ? incrementalSales / adSpend : 0;

            const formattedData = {
              lift_plot: {
                Time: liftPlot.Time || {},
                t_obs: liftPlot.t_obs || {},
                c_obs: liftPlot.c_obs || {},
              },
              att_plot: {
                Time: attPlot.Time || {},
                Estimate: attPlot.Estimate || {},
              },
              salesWithoutCampaign: totalSalesWithoutCampaign,
              pvalue: resultData.pvalue || 0,
              salesWithCampaign: totalSalesWithCampaign,
              lift_percentage: liftPercentage,
              incremental: incrementalSales,
              adSpend: adSpend,
              roas: roas,
              qc_openai: resultData['qc_openai'],
              isSales,
            };
            setPostTestData(formattedData);

            // Get the test markets from the API response
            const locations = Object.keys(response.data.result)
              .filter((key) => key.includes("/BestMarkets"))
              .map((key) => key.split("/")[0]);

            if (locations.length > 0) {
              setTestMarkets(locations);
              // Set the first location as selected by default
              setSelectedMarket(locations[0]);

              // Get market data for the selected location
              const marketDataMap: Record<string, MarketData[]> = {};
              locations.forEach((location) => {
                const key = `${location}/BestMarkets`;
                if (response.data.result[key]) {
                  marketDataMap[location] = response.data.result[key];
                }
              });
              setAllMarketData(marketDataMap);
              setMarketData(marketDataMap[locations[0]] || []);
            }
          }
        }
      } catch (error) {
        console.error("Error checking post-test data:", error);
      }
    };

    // Only call checkPostTestData if not a new job and not in edit mode
    const modeParamResult = searchParams.get("mode");
    const isEditModeResult = searchParams.get("edit") === "true";
    const isNewJobResult = !!modeParamResult && !isEditModeResult; // New job has mode but NOT edit=true

    console.log("GeoLiftStudy useEffect - mode:", modeParamResult, "edit:", isEditModeResult, "isNewJob:", isNewJobResult, "jobId:", jobId);

    if (!isNewJobResult && !isEditModeResult) {
      console.log("Calling checkPostTestData in GeoLiftStudy - view mode");
      checkPostTestData();
    } else {
      console.log("Skipping checkPostTestData in GeoLiftStudy - new job or edit mode");
    }
  }, [jobId, searchParams]);

  useEffect(() => {
    if (recommendationStatus && recommendationStatus !== "null") {
      if (recommendationStatus === "recommendation_success") {
        setCurrent(2);
      } else {
        setCurrent(steps.length - 1);
      }
    }
  }, [recommendationStatus]);

  // Track whether TestDesignChat has matched a design (used to style Execute Test button)
  const testDesignMatched = useSelector(selectHasMatched);
  const sessionFetched = useSelector(selectSessionFetched);
  const sessionLoading = useSelector(selectSessionLoading);
  const sessionExists = useSelector(selectSessionExists);

  // Fetch persisted test design session in view mode.
  // sessionFetchedForJobRef tracks the last-fetched jobId so study switches
  // trigger a fresh fetch. resetForStudySwitch() fires synchronously before
  // the async fetch so stale Step 0 from the previous study never flashes.
  useEffect(() => {
    if (!isViewMode) {
      dispatch(clearSession());
      return;
    }

    if (!jobId) return;

    // Same jobId already fetched — do nothing
    if (sessionFetchedForJobRef.current === jobId) return;
    sessionFetchedForJobRef.current = jobId;

    // Reset immediately: shows loader, hides any previous study's Step 0
    dispatch(resetForStudySwitch());

    const fetchSession = async () => {
      try {
        const response = await axiosInstance.get(`/api/test-design/session/${jobId}`);
        const { exists, messages } = response.data;
        if (exists && messages && messages.length > 0) {
          dispatch(setMessages(messages));
          dispatch(setHasMatched(true));
          dispatch(setSessionExists(true));
        } else {
          dispatch(setSessionExists(false));
        }
      } catch (err) {
        console.error('[TestDesign] session fetch failed', err);
        dispatch(setSessionExists(false));
      } finally {
        dispatch(setSessionLoading(false));
        dispatch(setSessionFetched(true));
      }
    };

    fetchSession();
  }, [jobId, isViewMode]);

  // Fetch buyType from the job config API when it's not available from navigation state.
  // This happens when the URL is opened directly (e.g., copied into a new tab).
  useEffect(() => {
    if (!isViewMode || resolvedBuyType !== undefined || !jobId) return;
    axiosInstance.get(`/api/jobConfig/${jobId}`)
      .then(response => {
        const bt = response.data?.modelParameterConfig?.parameters?.buyType || "non-national";
        setResolvedBuyType(bt);
      })
      .catch(() => setResolvedBuyType("non-national"));
  }, [isViewMode, jobId]);

  // After session fetch completes in view mode, land on step 0.
  // When Step 0 (test-design) is in the array → user sees test design tab.
  // When Step 0 is not in the array → user sees upload tab.
  // jobId in deps ensures step resets on study switch.
  useEffect(() => {
    if (!isViewMode || !sessionFetched) return;
    setCurrent(0);
  }, [sessionFetched, isViewMode, jobId]);

  // Load column mappings and capture initial data for edit/view mode
  const jobConfigList = useSelector((state: RootState) => state.geoLift.jobConfigList);

  useEffect(() => {
    const isEditOrViewMode = jobId && (edit === "true" || id);
    if (!isEditOrViewMode || jobConfigList.length === 0) return;

    const jobConfig = jobConfigList.find((config: any) => config.jobConfigId === jobId);
    if (!jobConfig) return;

    // Load column mappings once
    if (!columnMappingsLoaded && jobConfig?.dataMappingConfig?.mappings?.geoLift?.columnMapping) {
      const mapping = jobConfig.dataMappingConfig.mappings.geoLift.columnMapping;
      const kpiColumn = mapping.kpi_variables?.[0] || "Y";
      setColumnMappings({
        dateColumn: mapping.date_variables?.[0] || "date",
        locationColumn: mapping.location_variables?.[0] || "location",
        kpiColumn: kpiColumn.toLowerCase() === 'y' ? 'Y' : kpiColumn, // Normalize to uppercase
      });
      setColumnMappingsLoaded(true);
    }

    // Capture initial state once (after markets are loaded from DB)
    if (
      !hasInitialDataBeenCaptured.current &&
      selectedMarketsIncludes.length > 0 &&
      (preCampaignData.geoLiftStudyName || campaignDate)
    ) {
      const initialData = {
        geoLiftStudyName: preCampaignData.geoLiftStudyName,
        campaignStartDate: campaignDate?.format("YYYY-MM-DD"),
        scmType: scmType,
        regresionType: regressionType,
        isSales: isSales,
        selectedMarketsIncludes: [...selectedMarketsIncludes],
        fileListLength: preCampaignData.fileList.length,
      };
      setInitialPreCampaignData(initialData);
      hasInitialDataBeenCaptured.current = true;
    }
  }, [jobId, edit, id, jobConfigList, columnMappingsLoaded, selectedMarketsIncludes, preCampaignData.geoLiftStudyName, campaignDate, scmType, regressionType,preCampaignData.fileList.length]);

  // Calculate if data has changed using useMemo to avoid unnecessary re-renders
  const hasDataChangedValue = useMemo(() => {
    if (!initialPreCampaignData || !jobId) {
      return false;
    }

    const currentCampaignDate = campaignDate?.format("YYYY-MM-DD");
    const initialCampaignDate = initialPreCampaignData.campaignStartDate;

    // Compare primitive values first (fast)
    if (
      preCampaignData.geoLiftStudyName !== initialPreCampaignData.geoLiftStudyName ||
      currentCampaignDate !== initialCampaignDate ||
      scmType !== initialPreCampaignData.scmType ||
      regressionType !== initialPreCampaignData.regressionType ||
      preCampaignData.fileList.length !== initialPreCampaignData.fileListLength
    ) {
      return true;
    }

    // Compare arrays only if primitive checks passed (slower)
    if (selectedMarketsIncludes.length !== initialPreCampaignData.selectedMarketsIncludes.length) {
      return true;
    }

    // Deep comparison only if lengths match
    for (let i = 0; i < selectedMarketsIncludes.length; i++) {
      if (selectedMarketsIncludes[i] !== initialPreCampaignData.selectedMarketsIncludes[i]) {
        return true;
      }
    }

    return false;
  }, [
    initialPreCampaignData,
    jobId,
    preCampaignData.geoLiftStudyName,
    preCampaignData.fileList.length,
    campaignDate,
    scmType,
    regressionType,
    selectedMarketsIncludes,
  ]);

  // Update hasDataChanged only when the calculated value actually changes
  useEffect(() => {
    if (hasDataChangedValue !== hasDataChanged) {
      setHasDataChanged(hasDataChangedValue);
    }
  }, [hasDataChangedValue]);

  // Keep postTestData.isSales in sync when isSales is loaded from Redux
  useEffect(() => {
    if (postTestData) {
      setPostTestData((prev: any) => prev ? { ...prev, isSales } : prev);
    }
  }, [isSales]);

  const handleFormSubmit = async (formData: Record<string, any>) => {
    const isPreCampaignData =
      formData.fileList && formData.buyType && formData.geoLiftStudyName;
    if (!isPreCampaignData) {
      if (formData.includeMarket && Array.isArray(formData.includeMarket)) {
        formData.includeMarket = formData.includeMarket.join(";;;");
      }
      if (formData.excludeMarket && Array.isArray(formData.excludeMarket)) {
        formData.excludeMarket = formData.excludeMarket.join(";;;");
      }
    }

    try {
      // Use actual user column names from columnMappings state
      const locationVariable = columnMappings.locationColumn || "location";
      // Always normalize KPI variable to uppercase 'Y' to match CSV column name
      const rawKpiVariable = columnMappings.kpiColumn || "Y";
      const kpiVariable = rawKpiVariable.toLowerCase() === 'y' ? 'Y' : rawKpiVariable;
      const dateVariable = columnMappings.dateColumn || "date";

      console.log("Column mappings being used:", {
        dateVariable,
        locationVariable,
        kpiVariable,
        originalKpiColumn: columnMappings.kpiColumn
      });

      const fileSource = isPreCampaignData
        ? formData.fileList[0]
        : preCampaignData.fileList[0];
      const jobId = id === undefined ? uuidv4() : id;
      setJobId(jobId);

      // Only parse CSV if we have a file (new job or file changed)
      let durationType = "weeks"; // Default value
      if (fileSource) {
        const csvData = await new Promise<any[]>((resolve) => {
          Papa.parse(fileSource, {
            complete: (results) => resolve(results.data),
            header: true,
          });
        });
        const locations = csvData
          .map((row) => row[locationVariable])
          .filter((value, index, self) => self.indexOf(value) === index);
        const firstLocation = locations[0];
        const locationData = csvData.filter(
          (row) => row[locationVariable] === firstLocation
        );
        const dates = locationData.map((row) => {
          const dateStr = row[dateVariable];
          const format = "YYYY-MM-DD";
          const jsFormat = format
            .replace(/%Y/g, "YYYY")
            .replace(/%y/g, "YY")
            .replace(/%m/g, "MM")
            .replace(/%d/g, "DD")
            .replace(/%H/g, "HH")
            .replace(/%M/g, "mm")
            .replace(/%S/g, "ss");
          return dayjs(dateStr, jsFormat).toDate();
        });
        const sortedDates = dates
          .filter((d) => !isNaN(d.getTime()))
          .sort((a, b) => a.getTime() - b.getTime());
        let totalDiff = 0;
        for (let i = 1; i < sortedDates.length; i++) {
          totalDiff +=
            (sortedDates[i].getTime() - sortedDates[i - 1].getTime()) /
            (1000 * 60 * 60 * 24);
        }
        const avgDiffDays = totalDiff / (sortedDates.length - 1);
        durationType = Math.abs(avgDiffDays - 7) < 1 ? "weeks" : "days";
      }
      const studyName = isPreCampaignData
        ? formData.geoLiftStudyName
        : preCampaignData.geoLiftStudyName;
      const buyTypeValue = isPreCampaignData
        ? formData.buyType
        : mode === "evaluate"
        ? "national"
        : "non-national";

      const payload = {
        jobConfigId: jobId,
        name: studyName,
        jobType: JobType.GeoLift,
        is_ai_qc_enabled: preCampaignData.is_ai_qc_enabled,
        tenantId: getTenantId(),
        dataSourceConfig: {
          dataSourceType: "FILE_CSV",
        },
        dataTransformationConfig: {},
        dataMappingConfig: {
          mappings: {
            geoLift: {
              columnMapping: {
                date_variables: [dateVariable],
                kpi_variables: [kpiVariable],
                location_variables: [locationVariable],
              },
            },
          },
        },
        modelVersion: "V1",
        modelParameterConfig: {
          parameters: {
            dateFormat: "%Y-%m-%d",
            scmType: scmType,
            regressionType: regressionType,
            isSales,
            ...((mode === "evaluate" || buyTypeValue === "national") && (campaignDate || preCampaignData.campaignStartDate) && {
              campaignStartDate: campaignDate ? campaignDate.format("YYYY-MM-DD") : preCampaignData.campaignStartDate,
            }),
            buyType: buyTypeValue,
            durationType: durationType,
            ...(isPreCampaignData ? {} : formData),
            ...(scmType === "single"
              ? { includeMarket: selectedMarketsIncludes.map((ele) => ele) }
              : []),
            bauSpent: preCampaignData.bauSpent,
            statSigThreshold: preCampaignData.statSigThreshold,
          },
        },
        // Preserve the existing modelOutputConfig (which holds outputData like
        // aggregateLift, ROAS, etc.) so an edit doesn't wipe out computed results.
        modelOutputConfig: (() => {
          const existing = jobConfigList.find((c: any) => c.jobConfigId === jobId);
          return existing?.modelOutputConfig || {};
        })(),
      };
      // Detect job mode: new, edit, or view
      const modeParam = searchParams.get("mode");
      const isEditMode = edit === "true";
      const isNewJob = !!modeParam && !isEditMode; // New job has mode but NOT edit=true
      const isViewMode = !modeParam && !isEditMode && id && jobId; // View mode: no mode, no edit, but has id

      console.log("handleFormSubmit - Job type detection:", {
        isNewJob,
        isEditMode,
        isViewMode,
        modeParam,
        edit,
        id,
        jobId
      });

      // View mode: Don't submit anything, just return
      if (isViewMode) {
        console.log("View mode detected - no submission");
        return;
      }

      // Edit mode: Always update job config and trigger job run
      if (isEditMode) {
        console.log("Edit mode detected - updating job config and triggering job run");

        // Always update job config in edit mode
        await axiosInstance.put(`/api/jobConfig/${jobId}`, payload);

        // Upload file if user re-uploaded CSV
        if (fileSource) {
          const geoLiftFilePath = jobId + "/input/geo_lift_data.csv";
          await uploadFileToS3(fileSource, geoLiftFilePath);
        }

        // Always trigger job run in edit mode
        await axiosInstance.post(
          "/api/jobRun",
          { jobConfigId: jobId },
          {
            headers: {
              Authorization: "Bearer " + localStorage.getItem("authToken"),
            },
          }
        );
        console.log("Job run triggered successfully in edit mode");
        return;
      }

      // New job creation flow
      if (isNewJob) {
        console.log("New job detected - creating job config and triggering job run");

        // Only upload file if it exists (CSV upload case)
        // If fileSource is undefined, data was loaded from database and is already in S3
        if (fileSource) {
          const geoLiftFilePath = jobId + "/input/geo_lift_data.csv";
          await uploadFileToS3(fileSource, geoLiftFilePath);
          console.log("File uploaded to S3");
        } else {
          console.log("No file to upload - data was loaded from database");
        }

        await axiosInstance.post("/api/jobConfig", payload);
        await axiosInstance.post(
          "/api/jobRun",
          { jobConfigId: jobId },
          {
            headers: {
              Authorization: "Bearer " + localStorage.getItem("authToken"),
            },
          }
        );
        console.log("New job created successfully");
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleMarketChange = (value: string) => {
    setSelectedMarket(value);
    setMarketData(allMarketData[value] || []);
  };
  const handlePreCampaignChange = (values: {
    geoLiftStudyName: string;
    buyType: "national" | "non-national";
    fileList: File[];
    campaignStartDate?: string;
    totalSpent?: string;
    columnMappings?: { [key: string]: string | null }; // NEW: Accept mappings from PreCampaign
    is_ai_qc_enabled: boolean;
    bauSpent: number;
    statSigThreshold: number;
    isSales: boolean | undefined;
    campaignDateOutOfRange?: boolean;
  }) => {
    setPreCampaignData(values); // Preserve all PreCampaign data

    // NEW: If column mappings are provided from PreCampaign modal, use them
    if (values.columnMappings) {
      const mappings = {
        dateColumn: values.columnMappings.date || "date",
        locationColumn: values.columnMappings.location || "location",
        kpiColumn: values.columnMappings.kpi || "y",
      };
      setColumnMappings(mappings);

      // Parse CSV with the confirmed mappings
      if (values.fileList.length > 0) {
        const file: any = values.fileList[0];
        if (file) {
          Papa.parse(file, {
            header: true,
            complete: (result) => {
              const { data } = result;
              const parsedData = data
                .filter(
                  (item: any) =>
                    item[mappings.dateColumn] && item[mappings.locationColumn]
                )
                .map((item: any) => ({
                  date: item[mappings.dateColumn],
                  location: item[mappings.locationColumn].trim(),
                  value: parseFloat(item[mappings.kpiColumn]) || 0,
                }));

              setCsvData(parsedData);
            },
            error: (error) => {
              console.error("CSV parsing error:", error);
              setCsvData([]);
            },
          });
        }
      }
    }
    // LEGACY: Keep old auto-detection logic as fallback (for backward compatibility)
    else if (values.fileList.length > 0) {
      const file: any = values.fileList[0];
      if (file) {
        Papa.parse(file, {
          header: true,
          complete: (result) => {
            const { data, meta } = result;
            const autoDetectedColumns = {
              dateColumn:
                meta.fields?.find((f) => f.toLowerCase().includes("date")) ||
                "date",
              locationColumn:
                meta.fields?.find((f) =>
                  f.toLowerCase().includes("location")
                ) || "location",
              kpiColumn:
                meta.fields?.find((f) =>
                  f.toLowerCase().match(/y|sales|revenue/)
                ) || "y",
            };

            setColumnMappings(autoDetectedColumns);

            const parsedData = data
              .filter(
                (item: any) =>
                  item[autoDetectedColumns.dateColumn] &&
                  item[autoDetectedColumns.locationColumn]
              )
              .map((item: any) => ({
                date: item[autoDetectedColumns.dateColumn],
                location: item[autoDetectedColumns.locationColumn].trim(),
                value: parseFloat(item[autoDetectedColumns.kpiColumn]) || 0,
              }));

            setCsvData(parsedData);
          },
          error: (error) => {
            console.error("CSV parsing error:", error);
            setCsvData([]);
          },
        });
      }
    } else {
      setCsvData([]);
    }
  };

  const handleJobCreated = (newJobId: string) => {
    setJobId(newJobId);
  };
  const handleNext = async () => {
    // Test-design step: in view mode always go one step forward so the user
    // navigates sequentially. Only jump straight to results in create/edit flows
    // where skipping the analysis steps after results are ready is intentional.
    if (steps[current]?.id === "test-design") {
      if (!isViewMode) {
        const resultsIdx = steps.findIndex(s => s.id === 'results');
        if (postTestData && resultsIdx !== -1) {
          setCurrent(resultsIdx);
          scrollContentToTop();
          return;
        }
      }
      setCurrent(current + 1);
      scrollContentToTop();
      return;
    }

    const isEvaluateMode = mode === "evaluate";
    const isViewingRecommendation =
      recommendationStatus === "recommendation_success";

    const modeParam = searchParams.get("mode");
    const isNewJob = !!modeParam;
    const isExistingJob = edit === "true" || (!isNewJob && id && jobId);

    const isOnUploadStep = steps[current]?.id === "upload";
    const isOnVerifyStep = steps[current]?.id === "verify";

    console.log("handleNext - Debug info:", {
      current,
      stepId: steps[current]?.id,
      isEvaluateMode,
      isNewJob,
      isExistingJob,
      hasDataChanged,
      modeParam,
      edit,
      id,
      jobId,
    });

    if (!isViewingRecommendation) {
      if (isOnUploadStep) {
        // In view mode just advance — never submit, never reset market data.
        // Resetting refreshMarketData would cause ViewTestAndControlMarket to
        // enter a permanent loading state (refreshTrigger > 0 bails without fetching).
        if (isViewMode) {
          setCurrent(current + 1);
          scrollContentToTop();
          return;
        }

        if (isEvaluateMode && isExistingJob && !hasDataChanged) {
          console.log("No changes detected, navigating without submission");
          setCurrent(current + 1);
          scrollContentToTop();
          return;
        }

        if (!isEvaluateMode || !isExistingJob || hasDataChanged) {
          console.log("Submitting form with changes");
          await handleFormSubmit(preCampaignData);
          setAllMarketData({});
          setMarketData([]);
          setRefreshMarketData(prev => prev + 1);
        }

        setCurrent(current + 1);
        scrollContentToTop();
        return;
      } else if (isOnVerifyStep && !isEvaluateMode && verifyTestInputRef.current) {
        verifyTestInputRef.current.submitForm();
      }
    }

    if (current < steps.length - 1) {
      setCurrent(current + 1);
      scrollContentToTop();
    }
  };
  const handlePrev = () => {
    setCurrent((current) => Math.max(current - 1, 0)); // Ensure it doesn't go below 0
    // Scroll to top when moving to previous step
    scrollContentToTop();
  };

  const allSteps = [
    {
      id: 'test-design',
      title: 'Test Design',
      content: (
        <TestDesignChat
          jobConfigId={jobId}
          onFormSubmit={(data) => handleNext()}
          isViewMode={!mode && edit !== "true"}
        />
      ),
    },
    {
      id: 'upload',
      title: "Data Connect/Upload",
      content: (
        <PreCampaign
          ref={preCampaignRef}
          jobConfigId={jobId}
          fileData={fileData}
          onValuesChange={handlePreCampaignChange}
          initialValues={preCampaignData}
          setFileUploaded={setFileUploaded}
          setCampaignDate={setCampaignDate}
          mode={mode}
          setSelectedMarketsIncludes={setSelectedMarketsIncludes}
          selectedMarketsIncludes={selectedMarketsIncludes}
          scmType={scmType}
          setScmType={setScmType}
          regressionType={regressionType}
          setRegressionType={setRegressionType}
          isSales={isSales}
          setIsSales={setIsSales}
          viewMode={isViewMode}
        />
      ),
    },
    // {
    //   id: 'verify',
    //   title: "Verify test inputs",
    //   content: (
    //     <VerifyTestInput
    //       ref={verifyTestInputRef}
    //       jobConfigId={jobId}
    //       preCampaignData={preCampaignData}
    //       isNationalBuy={preCampaignData.buyType === "national"}
    //       csvData={csvData}
    //       dateColumn={columnMappings.dateColumn}
    //       locationColumn={columnMappings.locationColumn}
    //       onFormSubmit={handleFormSubmit}
    //       kpiColumn={columnMappings.kpiColumn}
    //       onJobCreated={handleJobCreated}
    //     />
    //   ),
    // },
    {
      id: 'power',
      title: "Power Analysis",
      content: (
        <ViewTestAndControlMarket
          testMarkets={testMarkets}
          selectedMarket={selectedMarket}
          jobConfigId={jobId}
          marketData={marketData}
          preCampaignData={preCampaignData}
          onMarketChange={handleMarketChange}
          onTestMarketsChange={setTestMarkets}
          buyType={buyType}
          mode={mode}
          refreshTrigger={refreshMarketData}
          isSales={isSales}
        />
      ),
    },
    // {
    //   id: 'post',
    //   title: "Upload Post campaign data",
    //   content: (
    //     <div>
    //       <PostTestControlMarketSelection
    //         jobConfigId={jobId}
    //         setPostTestData={setPostTestData}
    //         onNext={handleNext}
    //       />
    //     </div>
    //   ),
    // },
    {
      id: 'results',
      title: "Incrementality Results",
      content: (
        <ViewResults
          resultData={postTestData}
          jobConfigId={jobId}
          setPostTestData={setPostTestData}
          mode={mode}
        />
      ),
    },
    {
      id: 'ai',
      title: "AI Insights",
      content: (
        <AIAgentInsights
          jobId={jobId}
          isSales={isSales}
        />
      ),
    }

  ];

  const showTestDesignStep = (() => {
    if (isEditMode)   return false;
    if (isCreateMode) return true;
    if (isViewMode)   return sessionFetched && sessionExists;
    return false;
  })();

  // Keep refs in sync so async closures (checkPostTestData) read live values
  // rather than stale captures from when the effect first ran.
  useEffect(() => { currentRef.current = current; }, [current]);
  useEffect(() => { showTestDesignStepRef.current = showTestDesignStep; }, [showTestDesignStep]);

  const steps = useMemo(() => {
    const isEvaluateOrNational = mode === "evaluate" || resolvedBuyType === "national";

    // Define logic for which step IDs are allowed per mode
    let allowedIds: string[];

    if (isEvaluateOrNational) {
      allowedIds = ['test-design', 'upload', 'power', 'results', 'ai'];
    } else {
      allowedIds = ['upload', 'verify', 'power', 'post', 'results'];
    }

    return allSteps.filter(step => {
      if (step.id === "test-design" && !showTestDesignStep) return false;
      return allowedIds.includes(step.id);
    });
  }, [mode, resolvedBuyType, isEditMode, isCreateMode, isViewMode, sessionFetched, sessionExists, showTestDesignStep, allSteps]);

  const { Text } = Typography;

  if (isViewMode && (!sessionFetched || sessionLoading || resolvedBuyType === undefined)) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "60vh", gap: 16 }}>
        <Spin size="large" />
        <Text type="secondary">Loading test...</Text>
      </div>
    );
  }


  return (
    <div className="w-full bg-white" style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        {/* Stepper Section */}
        <div
          className="pr-4 ml-6 overflow-y-auto"
          style={{ width: "25%", flexShrink: 0, height: "100%" }}
        >
          {mode === "plan" ? (
            <h2 className="text-2xl font-semibold mt-5 mb-6 text-gray-900">
              {preCampaignData.geoLiftStudyName || JobName || "Geo Lift Study"}
            </h2>
          ) : (
            <h2 className="text-2xl font-semibold mt-5 mb-6 text-gray-900">
              {preCampaignData.geoLiftStudyName ||
                JobName ||
                "Synthetic Control Test"}
            </h2>
          )}

          <Steps
            direction="vertical"
            current={current}
            onChange={(value) => {
              setCurrent(value);
              scrollContentToTop();
            }}
            items={steps.map((step) => ({
              title: step.title,
              disabled: true,

              // description: step.description,
            }))}
            className="[&_.ant-steps-item]:mb-12 [&_.ant-steps-item-tail]:!top-3 [&_.ant-steps-item-tail]:!h-[100px] [&_.ant-steps-item-tail]:!w-[3px] [&_.ant-steps-item-icon]:!text-lg [&_.ant-steps-item-icon]:!w-8 [&_.ant-steps-item-icon]:!h-8 [&_.ant-steps-item-icon]:!font-bold [&_.ant-steps-item-icon]:!flex [&_.ant-steps-item-icon]:!items-center [&_.ant-steps-item-icon]:!justify-center [&_.ant-steps-item-title]:!font-medium [&_.ant-steps-item-title]:!text-base"
          />
        </div>

        {/* Content Section */}
        <div
          ref={contentContainerRef}
          style={{
            flex: 1,
            borderLeft: "1px solid #f0f0f0",
            overflow: steps[current]?.id === "test-design" ? "hidden" : "auto",
            padding: steps[current]?.id === "test-design" ? 0 : "24px 16px",
            height: "100%",
            boxSizing: "border-box",
          }}
        >
          {steps[current]?.id === "test-design" ? (
            <div style={{ height: "100%" }}>{steps[current].content}</div>
          ) : (
            <div style={{ paddingBottom: 72 }}>{steps[current].content}</div>
          )}
        </div>
      </div>
      {/* Footer — in-flow (not fixed) so it doesn't cover content */}
      <div className="bg-white border-t border-gray-200 p-4 flex justify-between" style={{ flexShrink: 0 }}>
        <div>
          {current > 0 && (
            <Button
              onClick={handlePrev}
              size="large"
              className="mr-2"
              icon={<span className="mr-1">←</span>}
            >
              Previous
            </Button>
          )}
        </div>

        <div>
          {(isViewMode
            ? (steps[current]?.id !== 'ai')
            : current < steps.length - 1) && (
            <Button
              type="primary"
              size="large"
              onClick={handleNext}
              disabled={isNextDisabled()}
              icon={<span className="ml-1">→</span>}
            >
              {getNextButtonLabel()}
            </Button>
          )}
          {(isViewMode
            ? steps[current]?.id === 'ai'
            : current === steps.length - 1) && (
            <Button
              type="primary"
              size="large"
              onClick={() => navigate("/measurement/geo-lift")}
            >
              Finish
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GeoLiftStudy;
